import React, { useCallback, useState } from 'react';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import {
  useLazyGetYourDayBloomQuery,
  useArchiveBloomRockMutation,
  useCompleteBloomTodoMutation,
  useLazyGetArchivedBloomRocksQuery,
  useRestoreBloomRockMutation,
} from '../app/api';
import { msalInstance, graphMailRequest } from '../msalConfig';
import useGraphToken, { resolveAccount } from './useGraphToken';
import { useIsMobile } from './utils';

/* Bloom Growth — your open rocks and to-dos, and the two writes the hub makes.
 *
 * Read live from Bloom's API through an IT service account. This is the reason
 * Bloom's own notification email is muted: the work is better shown as current
 * state than as a pile of "X assigned you a to-do" mail.
 *
 * Lifted out of Your Day (now At A Glance), where it was the third tab, when it
 * earned its own place in the menu. The behaviour is unchanged; what moved is
 * where it lives.
 *
 * WHY A GRAPH TOKEN ON A PAGE THAT NEVER CALLS GRAPH: the API validates the
 * delegated token purely as proof of identity, so this endpoint isn't a softer
 * way past the same pilot gate that guards the briefing. It's the READ scope, so
 * no extra consent is involved.
 *
 * Three silent, legitimate reasons this page can come back empty: no Bloom
 * service account configured, Bloom unreachable, or this person not found in any
 * meeting the service account can see. Each says which, because "no rocks" and
 * "we couldn't look" are very different messages.
 */

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
};

const PATH = '/bloom';
// Minimum touch target. 44px is the Apple/WCAG floor; anything smaller is a
// coin-flip with a thumb.
const TAP = 44;
const WRAP = { minWidth: 0, overflowWrap: 'anywhere' };

const cardStyle = {
  background: 'white',
  border: 'none',
  borderRadius: 12,
  boxShadow: '0 2px 12px rgba(10,41,71,0.08)',
  overflow: 'hidden',
};

export default function Bloom() {
  const isMobile = useIsMobile();

  const [triggerBloom, bloomQ] = useLazyGetYourDayBloomQuery();
  const [archiveRockReq] = useArchiveBloomRockMutation();
  const [completeTodoReq] = useCompleteBloomTodoMutation();
  const [restoreRockReq] = useRestoreBloomRockMutation();
  const [loadArchived, archivedQ] = useLazyGetArchivedBloomRocksQuery();

  const bloomData = bloomQ.data;
  // Items cleared during this page's life, keyed 'kind:id'. Held locally because
  // the board is a live read of a third-party API that takes a couple of seconds
  // to re-read: hiding the row at once and refetching behind it beats leaving
  // something on screen that the user has already dealt with. Cleared on reload,
  // by which point Bloom is the source of truth again.
  const [bloomCleared, setBloomCleared] = useState({});
  // The item awaiting confirmation: { kind, id, title }. Null when no dialog.
  const [bloomConfirm, setBloomConfirm] = useState(null);
  const [bloomBusy, setBloomBusy] = useState('');
  const [bloomError, setBloomError] = useState('');
  // Undoing an archive. Collapsed by default and fetched on first open — a rare
  // recovery path shouldn't cost every reader a Bloom call.
  const [archivedOpen, setArchivedOpen] = useState(false);

  const onToken = useCallback((token, { preferCache = true } = {}) => {
    triggerBloom({ graphToken: token }, preferCache);
  }, [triggerBloom]);

  const { phase, authError, connecting, needsReload, connect, hubEmail } =
    useGraphToken(graphMailRequest, PATH, onToken);

  const isCleared = (kind, id) => Boolean(bloomCleared[`${kind}:${id}`]);
  const bloomRocks = (bloomData?.rocks || []).filter((r) => !isCleared('rock', r.id));
  const bloomTodos = (bloomData?.todos || []).filter((t) => !isCleared('todo', t.id));
  // Only offered when an admin has switched it on. A control that always 403s is
  // worse than no control, and the server re-checks the same switch anyway.
  const canActOnBloom = bloomData?.can_act === true;
  const bloomHasItems = bloomRocks.length > 0 || bloomTodos.length > 0;
  const bloomClearedCount = Object.keys(bloomCleared).length;

  /* The token every Bloom action rides on.
   *
   * These endpoints want the same READ-scope token the board was loaded with —
   * they make no Graph call of their own and validate the token purely as proof
   * of identity, so no extra consent is involved. Silent acquisition therefore
   * all but always succeeds, which is why there's no interactive fallback here:
   * the page cannot have rendered a board without this token having worked.
   */
  const bloomToken = useCallback(async () => {
    const account = resolveAccount();
    const req = account
      ? { ...graphMailRequest, account }
      : { ...graphMailRequest, loginHint: hubEmail };
    const { accessToken } = await msalInstance.acquireTokenSilent(req);
    return accessToken;
  }, [hubEmail]);

  /* Archive a rock, or complete a to-do, once the dialog is confirmed.
   *
   * The row is hidden on success and the board refetched behind it, so the counts
   * in the header come back from Bloom rather than being decremented on faith.
   */
  const runBloomAction = useCallback(async ({ kind, id }) => {
    if (!id) return;
    setBloomBusy(`${kind}:${id}`);
    setBloomError('');
    try {
      const accessToken = await bloomToken();
      if (kind === 'rock') {
        await archiveRockReq({ graphToken: accessToken, rockId: id }).unwrap();
      } else {
        await completeTodoReq({ graphToken: accessToken, todoId: id }).unwrap();
      }
      setBloomCleared((prev) => ({ ...prev, [`${kind}:${id}`]: true }));
      setBloomConfirm(null);
      // Bloom's own board cache was dropped server-side by the write, so this
      // re-reads the real state. Not awaited: the row is already gone.
      triggerBloom({ graphToken: accessToken }, false);
      // A rock just archived belongs in the archived list, so anything already
      // loaded there is now stale.
      if (kind === 'rock' && archivedOpen) {
        loadArchived({ graphToken: accessToken }, false);
      }
    } catch (e) {
      setBloomError(
        e?.data?.detail
        || (kind === 'rock' ? 'That rock could not be archived.' : 'That to-do could not be completed.')
      );
    } finally {
      setBloomBusy('');
    }
  }, [archiveRockReq, completeTodoReq, triggerBloom, bloomToken, archivedOpen, loadArchived]);

  /* Open (or close) the archived-rocks list, fetching on first open.
   *
   * Not loaded with the board: on most days nobody wants it, and it is one more
   * Bloom round-trip on a page that already makes several.
   */
  const toggleArchived = useCallback(async () => {
    if (archivedOpen) { setArchivedOpen(false); return; }
    setArchivedOpen(true);
    setBloomError('');
    try {
      const accessToken = await bloomToken();
      await loadArchived({ graphToken: accessToken }, true).unwrap();
    } catch (e) {
      setBloomError(e?.data?.detail || 'Your archived rocks could not be loaded.');
    }
  }, [archivedOpen, bloomToken, loadArchived]);

  /* Put an archived rock back.
   *
   * No confirmation: restoring is the recovery from a mistake, and making someone
   * confirm their way out of one they already regret is friction for its own
   * sake. It is also the one action here whose inverse is a click away.
   */
  const restoreRock = useCallback(async (rock) => {
    if (!rock?.id) return;
    setBloomBusy(`restore:${rock.id}`);
    setBloomError('');
    try {
      const accessToken = await bloomToken();
      await restoreRockReq({ graphToken: accessToken, rockId: rock.id }).unwrap();
      // If it was archived in THIS page's session it is still in `bloomCleared`,
      // which would hide it from the open list the moment Bloom hands it back.
      setBloomCleared((prev) => {
        const next = { ...prev };
        delete next[`rock:${rock.id}`];
        return next;
      });
      loadArchived({ graphToken: accessToken }, false);
      triggerBloom({ graphToken: accessToken }, false);
    } catch (e) {
      setBloomError(e?.data?.detail || 'That rock could not be restored.');
    } finally {
      setBloomBusy('');
    }
  }, [bloomToken, restoreRockReq, loadArchived, triggerBloom]);

  // "Not linked" and "nothing due" are very different messages, so the unmatched
  // case gets its own line rather than rendering as an empty board.
  const bloomUnlinked = bloomData?.configured && !bloomData?.unavailable && !bloomData?.matched;
  // Bloom is off, has no service account, or is unreachable. Distinct from
  // "unlinked", which is about this person rather than the integration.
  const bloomOff = bloomQ.isError
    || (bloomData && (!bloomData.configured || bloomData.unavailable));

  // A bare icon button — the X on a rock and the tick-box on a to-do. Styled here
  // rather than as a Bootstrap Button so it can sit flush in the row without the
  // padding and border a real button brings.
  const bloomActionBtn = ({ kind, item, icon, title }) => {
    const busy = bloomBusy === `${kind}:${item.id}`;
    return (
      <button
        type="button"
        aria-label={title}
        title={title}
        disabled={busy || !item.id}
        onClick={() => setBloomConfirm({ kind, id: item.id, title: item.title })}
        style={{
          background: 'none', border: 'none', padding: '0 0.15rem', lineHeight: 1,
          color: brand.charcoal, opacity: busy ? 0.5 : 0.65,
          cursor: busy ? 'default' : 'pointer', flexShrink: 0,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = busy ? 0.5 : 1; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = busy ? 0.5 : 0.65; }}
      >
        {busy
          ? <Spinner animation="border" size="sm" style={{ width: '0.8rem', height: '0.8rem' }} />
          : <i className={`bi ${icon}`} style={{ fontSize: '0.9rem' }} />}
      </button>
    );
  };

  /* `action` is 'archive' (rocks) or 'complete' (to-dos) or null when the feature
   * is off. The two are placed differently on purpose: a to-do's tick box
   * REPLACES its decorative checkbox icon on the left, where a checkbox belongs,
   * while a rock keeps its bullseye and gets a dismissing X on the trailing edge.
   * Same reason the glyphs differ — archiving a rock and completing a to-do are
   * not the same act, so they shouldn't look alike. */
  const bloomList = (items, icon, emptyNote, showMeeting, action) => (
    items.length === 0
      ? <div style={{ color: brand.charcoal, fontSize: '0.8rem', opacity: 0.8 }}>{emptyNote}</div>
      : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
          {items.map((it, i) => (
            <div key={it.id || i} style={{
              background: brand.softGray, borderRadius: 8,
              borderLeft: `3px solid ${brand.tealBlue}`,
              padding: '0.4rem 0.6rem',
              display: 'flex', alignItems: 'flex-start', gap: '0.4rem',
            }}>
              {action === 'complete' && bloomActionBtn({
                kind: 'todo', item: it, icon: 'bi-square',
                title: `Mark “${it.title}” complete in Bloom`,
              })}
              <div style={{ flex: 1, minWidth: 0, ...WRAP }}>
                <div style={{ color: brand.deepBlue, fontWeight: 700, fontSize: '0.82rem', ...WRAP }}>
                  {action !== 'complete' && (
                    <i className={`bi ${icon}`} style={{ marginRight: '0.35rem', opacity: 0.75 }} />
                  )}
                  {it.title}
                </div>
                {(it.due || (showMeeting && it.meeting)) && (
                  <div style={{ color: brand.charcoal, fontSize: '0.74rem', ...WRAP }}>
                    {it.due ? `Due ${new Date(it.due).toLocaleDateString()}` : ''}
                    {it.due && showMeeting && it.meeting ? ' · ' : ''}
                    {showMeeting && it.meeting ? it.meeting : ''}
                  </div>
                )}
              </div>
              {action === 'archive' && bloomActionBtn({
                kind: 'rock', item: it, icon: 'bi-x-lg',
                title: `Archive “${it.title}” in Bloom`,
              })}
            </div>
          ))}
        </div>
      )
  );

  const card = (children) => (
    <div style={{ ...cardStyle, padding: isMobile ? '0.9rem' : '1.1rem 1.25rem' }}>
      {children}
    </div>
  );

  let body;
  if (phase === 'connect') {
    body = (
      <div style={{ ...cardStyle, padding: isMobile ? '1.25rem' : '1.75rem', textAlign: 'center' }}>
        <i className="bi bi-flower1" style={{ color: brand.tealBlue, fontSize: '2rem' }} />
        <h5 style={{ color: brand.deepBlue, fontWeight: 800, marginTop: '0.75rem' }}>
          Connect Microsoft
        </h5>
        <p style={{ color: brand.charcoal, fontSize: '0.9rem', maxWidth: 460,
                    margin: '0 auto 1rem', ...WRAP }}>
          Your Bloom board is read through the firm&apos;s service account, but we
          still need you signed in to Microsoft to prove who is asking. Nothing
          from your mailbox is read on this page.
        </p>
        {hubEmail && (
          <p style={{ color: brand.charcoal, fontSize: '0.8rem', marginBottom: '0.75rem', ...WRAP }}>
            Connecting <strong style={{ color: brand.deepBlue }}>{hubEmail}</strong>
          </p>
        )}
        {needsReload ? (
          <button type="button" onClick={() => window.location.reload()}
            style={{ minHeight: TAP, padding: '0.6rem 1.4rem', borderRadius: 8,
                     border: 'none', background: brand.deepBlue, color: 'white',
                     fontWeight: 800, cursor: 'pointer' }}>
            <i className="bi bi-arrow-clockwise me-1" /> Reload and try again
          </button>
        ) : (
          <button type="button" onClick={() => connect(isMobile)} disabled={connecting}
            style={{ minHeight: TAP, width: isMobile ? '100%' : 'auto',
                     padding: '0.6rem 1.4rem', borderRadius: 8, border: 'none',
                     background: connecting ? brand.softGray : brand.goldenrod,
                     color: connecting ? brand.charcoal : brand.deepBlue,
                     fontWeight: 800, cursor: connecting ? 'default' : 'pointer' }}>
            {connecting ? 'Waiting for Microsoft…' : 'Connect Microsoft'}
          </button>
        )}
        {authError && (
          <Alert variant="warning" className="py-2 mt-3 mb-0"
                 style={{ fontSize: '0.82rem', ...WRAP }}>
            {authError}
          </Alert>
        )}
      </div>
    );
  } else if (phase === 'loading' || !bloomData) {
    body = card(
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: brand.charcoal, fontSize: '0.85rem' }}>
        <Spinner animation="border" size="sm" style={{ color: brand.tealBlue }} />
        Reading your Bloom board…
      </div>
    );
  } else if (bloomOff) {
    // Not an error state to shout about: most environments simply have no Bloom
    // service account, and Bloom being down is Bloom's business, not a hub fault.
    body = card(
      <div style={{ color: brand.charcoal, fontSize: '0.85rem', ...WRAP }}>
        <i className="bi bi-flower1" style={{ marginRight: '0.4rem', opacity: 0.7 }} />
        Bloom isn&apos;t available right now. If this doesn&apos;t clear up, ask IT
        whether the Bloom service account is configured.
      </div>
    );
  } else if (bloomUnlinked) {
    body = card(
      <div style={{ color: brand.charcoal, fontSize: '0.85rem', ...WRAP }}>
        No Bloom account matched yet. Bloom doesn’t use single sign-on, so we
        check every address we know you by — including your pre-merger one. If
        you signed up under something else, ask IT to add it as an alias. You
        also need to be an attendee on a meeting the service account can see.
      </div>
    );
  } else {
    body = card(
      <>
        {bloomError && (
          <Alert variant="warning" className="py-2" style={{ fontSize: '0.82rem' }}
            dismissible onClose={() => setBloomError('')}>
            {bloomError}
          </Alert>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem', ...WRAP }}>
          <span style={{ color: brand.deepBlue, fontWeight: 800, fontSize: '0.78rem',
                         textTransform: 'uppercase', letterSpacing: '1px' }}>
            Rocks
          </span>
          {bloomHasItems && (
            <span style={{ marginLeft: 'auto', color: brand.charcoal, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
              {bloomData.rock_total} rock{bloomData.rock_total === 1 ? '' : 's'} · {bloomData.todo_total} to-do{bloomData.todo_total === 1 ? '' : 's'}
            </span>
          )}
        </div>
        {bloomList(bloomRocks, 'bi-bullseye', 'No open rocks.', false,
                   canActOnBloom ? 'archive' : null)}
        <div style={{ color: brand.deepBlue, fontWeight: 800, fontSize: '0.78rem',
                      textTransform: 'uppercase', letterSpacing: '1px',
                      margin: '0.7rem 0 0.35rem' }}>
          To-Dos
        </div>
        {bloomList(bloomTodos, 'bi-check2-square', 'No open to-dos.', true,
                   canActOnBloom ? 'complete' : null)}
        {bloomClearedCount > 0 && (
          <div style={{ color: brand.green, fontSize: '0.74rem', marginTop: '0.5rem' }}>
            <i className="bi bi-check2-circle" style={{ marginRight: '0.3rem' }} />
            {bloomClearedCount === 1 ? '1 item' : `${bloomClearedCount} items`} cleared in Bloom.
          </div>
        )}
        {(bloomData.rock_total > bloomRocks.length || bloomData.todo_total > bloomTodos.length) && (
          <div style={{ color: brand.charcoal, fontSize: '0.72rem', marginTop: '0.5rem', opacity: 0.8 }}>
            Showing the soonest due. Open Bloom for the full list.
          </div>
        )}
        {/* Undo for the X. Deliberately quiet and last: it exists to recover from
            a mistake, so it shouldn't compete with the actual work. Rocks only —
            Bloom has no archive for to-dos, so nothing to undo. */}
        {canActOnBloom && (
          <>
            <button
              type="button"
              onClick={toggleArchived}
              style={{
                background: 'none', border: 'none', padding: '0.4rem 0 0',
                color: brand.tealBlue, fontSize: '0.74rem', fontWeight: 600,
                cursor: 'pointer', minHeight: TAP, textAlign: 'left',
              }}
            >
              <i className={`bi ${archivedOpen ? 'bi-chevron-down' : 'bi-chevron-right'}`}
                 style={{ marginRight: '0.3rem' }} />
              Archived rocks
              {archivedQ.data ? ` (${archivedQ.data.rocks.length})` : ''}
              {archivedOpen && archivedQ.isFetching && (
                <Spinner animation="border" size="sm" className="ms-2"
                         style={{ width: '0.7rem', height: '0.7rem' }} />
              )}
            </button>
            {archivedOpen && archivedQ.data && (
              archivedQ.data.rocks.length === 0
                ? (
                  <div style={{ color: brand.charcoal, fontSize: '0.76rem', opacity: 0.8 }}>
                    Nothing archived.
                  </div>
                )
                : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                    {archivedQ.data.rocks.map((r) => {
                      const busy = bloomBusy === `restore:${r.id}`;
                      return (
                        <div key={r.id} style={{
                          background: 'transparent', borderRadius: 8,
                          border: `1px dashed ${brand.charcoal}55`,
                          padding: '0.35rem 0.55rem',
                          display: 'flex', alignItems: 'flex-start', gap: '0.4rem',
                        }}>
                          <div style={{ flex: 1, minWidth: 0, ...WRAP }}>
                            <div style={{ color: brand.charcoal, fontWeight: 600,
                                          fontSize: '0.78rem', ...WRAP }}>
                              {r.title}
                            </div>
                            {r.due && (
                              <div style={{ color: brand.charcoal, fontSize: '0.72rem', opacity: 0.8 }}>
                                Was due {new Date(r.due).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => restoreRock(r)}
                            disabled={busy}
                            title={`Restore “${r.title}” to your Bloom board`}
                            style={{
                              background: 'none', border: 'none', padding: '0 0.15rem',
                              color: brand.tealBlue, fontSize: '0.74rem', fontWeight: 700,
                              cursor: busy ? 'default' : 'pointer', flexShrink: 0,
                              opacity: busy ? 0.5 : 1, whiteSpace: 'nowrap',
                            }}
                          >
                            {busy
                              ? <Spinner animation="border" size="sm"
                                         style={{ width: '0.7rem', height: '0.7rem' }} />
                              : <><i className="bi bi-arrow-counterclockwise" /> Restore</>}
                          </button>
                        </div>
                      );
                    })}
                    <div style={{ color: brand.charcoal, fontSize: '0.7rem', opacity: 0.8 }}>
                      Restoring puts a rock back on every L10 board it came from.
                    </div>
                  </div>
                )
            )}
          </>
        )}
      </>
    );
  }

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh',
                  padding: isMobile ? '0.75rem' : '1.5rem' }}>
      <div style={{
        background: brand.deepBlue, borderRadius: 12,
        padding: isMobile ? '1.25rem' : '1.75rem 2rem',
        marginBottom: isMobile ? '1rem' : '1.5rem',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%',
                      height: 3, background: brand.goldenrod }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', ...WRAP }}>
          <div style={{ background: 'rgba(208,155,44,0.15)',
                        border: `2px solid ${brand.goldenrod}`, borderRadius: 12,
                        padding: '0.75rem', flexShrink: 0 }}>
            <i className="bi bi-flower1"
               style={{ color: brand.goldenrod, fontSize: '1.75rem' }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <p style={{ color: brand.goldenrod, fontWeight: 700, fontSize: '0.75rem',
                        textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 4px' }}>
              Bloom Growth
            </p>
            <h3 style={{ color: 'white', fontWeight: 800, margin: '0 0 4px',
                         fontSize: isMobile ? '1.25rem' : undefined }}>
              Rocks &amp; To-Dos
            </h3>
            <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem', ...WRAP }}>
              Live from Bloom — the X and the tick box write straight back
            </span>
          </div>
        </div>
      </div>

      {body}

      {/* The dialog says what the write actually DOES, on both boards, because
          archiving a rock removes it from every L10 it was on and completing a
          to-do tells that meeting it is done. Saying "are you sure?" without
          saying what happens is how someone archives a quarterly commitment by
          accident. */}
      <Modal show={Boolean(bloomConfirm)} centered
        onHide={() => { setBloomConfirm(null); setBloomError(''); }}>
        <Modal.Header closeButton>
          <Modal.Title style={{ color: brand.deepBlue, fontWeight: 800, fontSize: '1.05rem' }}>
            {bloomConfirm?.kind === 'rock' ? 'Archive this rock?' : 'Mark this to-do complete?'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{
            background: brand.softGray, borderRadius: 8,
            borderLeft: `3px solid ${brand.tealBlue}`,
            padding: '0.5rem 0.7rem', marginBottom: '0.85rem',
            color: brand.deepBlue, fontWeight: 700, fontSize: '0.88rem', ...WRAP,
          }}>
            {bloomConfirm?.title}
          </div>
          {bloomConfirm?.kind === 'rock' ? (
            <p style={{ color: brand.charcoal, fontSize: '0.86rem', margin: 0 }}>
              This archives the rock in Bloom, so it leaves <strong>every L10 board
              it was on</strong> — not just this page. Your team will stop seeing it.
              If that was a mistake, you can restore it in Bloom under
              Rocks → Archived.
            </p>
          ) : (
            <p style={{ color: brand.charcoal, fontSize: '0.86rem', margin: 0 }}>
              Bloom has no archive for to-dos, so this marks it
              <strong> genuinely complete</strong>. Your meeting will see it as done
              and it counts toward that L10’s to-do completion percentage. Reopen it
              in Bloom if you need it back.
            </p>
          )}
          <p style={{ color: brand.charcoal, fontSize: '0.76rem', margin: '0.75rem 0 0', opacity: 0.85 }}>
            Bloom logs changes under the firm’s service account, so the hub records
            that this was you.
          </p>
          {/* Shown here as well as on the board: a failure leaves this dialog
              open, and the banner behind it would be invisible under the backdrop. */}
          {bloomError && (
            <Alert variant="warning" className="py-2 mt-3 mb-0" style={{ fontSize: '0.82rem' }}>
              {bloomError}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light"
            onClick={() => { setBloomConfirm(null); setBloomError(''); }}>Cancel</Button>
          <Button
            variant={bloomConfirm?.kind === 'rock' ? 'danger' : 'success'}
            disabled={Boolean(bloomBusy)}
            onClick={() => runBloomAction(bloomConfirm)}
          >
            {bloomBusy
              ? <><Spinner animation="border" size="sm" /> Working…</>
              : (bloomConfirm?.kind === 'rock' ? 'Archive in Bloom' : 'Mark complete')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
